import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";

/**
 * OTPModal — Tactical Brutalism email-OTP verification flow.
 *
 * Two-step state machine:
 *   1. EMAIL  — user types/confirms email, clicks SEND CODE → POST /auth/request-otp
 *   2. CODE   — user types 6-digit code, clicks VERIFY → POST /auth/verify-otp
 * On success the parent receives `{ email, verification_token }` via onVerified().
 *
 * Props:
 *   open          : bool
 *   onClose       : () => void
 *   onVerified    : ({ email, verification_token }) => void
 *   title         : string — banner text (e.g. "ADMIN EMAIL VERIFICATION")
 *   subtitle      : string — optional contextual line under the title
 *   lockedEmail   : string|null — when set, email field is locked & step 1 auto-submits
 *   ctaSendLabel  : string — defaults to "SEND CODE"
 *   ctaVerifyLabel: string — defaults to "VERIFY →"
 */
export default function OTPModal({
  open,
  onClose,
  onVerified,
  title = "EMAIL VERIFICATION",
  subtitle = "WE WILL EMAIL YOU A 6-DIGIT CODE.",
  lockedEmail = null,
  ctaSendLabel = "SEND CODE",
  ctaVerifyLabel = "VERIFY →",
}) {
  const [step, setStep] = useState("email"); // "email" | "code"
  const [email, setEmail] = useState(lockedEmail || "");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (open) {
      // Reset modal state when re-opened — canonical React pattern.
      setStep("email");
      setEmail(lockedEmail || "");
      setCode("");
      setError("");
      setInfo("");
      setBusy(false);
    }
  }, [open, lockedEmail]);

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError("ENTER A VALID EMAIL");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.requestOtp(trimmed);
      setInfo(`CODE SENT TO ${trimmed.toUpperCase()}`);
      setEmail(trimmed);
      setStep("code");
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      setError(detail.toUpperCase() || "COULDN'T SEND CODE");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) {
      setError("ENTER THE 6-DIGIT CODE");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api.verifyOtp(email, code);
      onVerified?.({ email: res.email, verification_token: res.verification_token });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      setError(detail.toUpperCase() || "INVALID OR EXPIRED CODE");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setStep("email");
    setError("");
    setInfo("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-md bg-[var(--stakes-surface)] border-2 border-[var(--neon-blue)] rounded-none p-0 [&>button]:hidden"
        data-testid="otp-modal"
        style={{ boxShadow: "0 0 32px rgba(0,229,255,0.18), inset 0 0 60px rgba(0,229,255,0.04)" }}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">{subtitle}</DialogDescription>
        <div className="p-6 md:p-8 border-b-2 border-[var(--neon-blue)]">
          <div className="label-mono" style={{ color: "var(--neon-blue)" }}>{title}</div>
          <h2 className="font-anton text-3xl md:text-4xl uppercase leading-none mt-2">
            {step === "email" ? "Enter Your Email" : "Enter The Code"}
          </h2>
          <div className="label-mono mt-3 text-[var(--stakes-muted)]">{subtitle}</div>
        </div>

        {step === "email" ? (
          <form onSubmit={requestOtp} className="p-6 md:p-8 space-y-5">
            <div>
              <div className="label-mono mb-1">EMAIL</div>
              <input
                type="email"
                autoFocus
                disabled={!!lockedEmail || busy}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                placeholder="you@stakesleague.com"
                className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--neon-blue)] outline-none font-mono uppercase text-xl pb-2 disabled:opacity-70"
                data-testid="otp-email-input"
                autoComplete="email"
              />
            </div>
            {error && (
              <div className="label-mono" style={{ color: "var(--stakes-primary)" }} data-testid="otp-error">
                {error}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="flex-1 h-12 label-mono border-2 border-[var(--stakes-border)] hover:bg-white hover:!text-black hover-flip disabled:opacity-40"
                data-testid="otp-cancel-button"
              >
                ← CANCEL
              </button>
              <button
                type="submit"
                disabled={busy || !email}
                className="flex-1 h-12 bg-[var(--neon-blue)] !text-black font-mono font-black uppercase tracking-wider border-2 border-[var(--neon-blue)] hover:bg-black hover:!text-white hover:border-white hover-flip disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="otp-send-button"
              >
                {busy ? "SENDING…" : ctaSendLabel}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={verify} className="p-6 md:p-8 space-y-5">
            {info && (
              <div className="label-mono" style={{ color: "var(--neon-blue)" }} data-testid="otp-info">
                {info}
              </div>
            )}
            <div>
              <div className="label-mono mb-1">6-DIGIT CODE</div>
              <input
                autoFocus
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
                placeholder="------"
                className="w-full bg-transparent border-b-2 border-[var(--stakes-border)] focus:border-[var(--neon-pink)] outline-none font-anton text-5xl text-center tracking-[0.4em] pb-2"
                data-testid="otp-code-input"
                autoComplete="one-time-code"
              />
            </div>
            {error && (
              <div className="label-mono" style={{ color: "var(--stakes-primary)" }} data-testid="otp-error">
                {error}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={resend}
                disabled={busy}
                className="flex-1 h-12 label-mono border-2 border-[var(--stakes-border)] hover:bg-white hover:!text-black hover-flip disabled:opacity-40"
                data-testid="otp-resend-button"
              >
                ↻ RESEND
              </button>
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="flex-1 h-12 bg-[var(--neon-pink)] !text-black font-mono font-black uppercase tracking-wider border-2 border-[var(--neon-pink)] hover:bg-black hover:!text-white hover:border-white hover-flip disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="otp-verify-button"
              >
                {busy ? "VERIFYING…" : ctaVerifyLabel}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
